import React, { useEffect, useState } from 'react';

/**
 * The page index, replacing the old footer.
 *
 * A fixed rail rather than a block at the bottom, so the links are reachable the
 * whole way down instead of only after the reader has finished. It reuses the
 * section numbers the page already prints, so it reads as the table of contents
 * for something already numbered rather than as a second navigation.
 *
 * The one complication: it sits over sections that alternate between the cream
 * and night grounds. Rather than a blend mode, which turns muddy over
 * photographs, it tracks which section holds the vertical centre of the viewport
 * and takes that section's ink or cream.
 */

const SECTIONS = [
  { n: '01', label: 'Aerveil', id: 'aerveil', dark: false },
  { n: '02', label: 'Experience', id: 'experience', dark: false },
  { n: '03', label: 'Work', id: 'work', dark: false },
  { n: '04', label: 'Publications', id: 'publications', dark: true },
  { n: '05', label: 'Film', id: 'film', dark: true },
  { n: '06', label: 'Contact', id: 'contact', dark: false },
];

const SectionRail = () => {
  const [active, setActive] = useState(null);
  const [onDark, setOnDark] = useState(true); // the hero is the night ground

  useEffect(() => {
    // Fires as a section crosses the vertical centre of the viewport, which is a
    // steadier read of "where am I" than top-edge intersection.
    const opts = { rootMargin: '-50% 0px -50% 0px', threshold: 0 };

    const sectionObs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (!e.isIntersecting) return;
        const s = SECTIONS.find((x) => x.id === e.target.id);
        if (s) {
          setActive(s.id);
          setOnDark(s.dark);
        }
      });
    }, opts);

    const observed = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    observed.forEach((el) => sectionObs.observe(el));

    // Above the first section: nothing is current, and the ground is dark.
    const hero = document.getElementById('top');
    const heroObs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setActive(null);
        setOnDark(true);
      }
    }, opts);
    if (hero) heroObs.observe(hero);

    return () => {
      sectionObs.disconnect();
      heroObs.disconnect();
    };
  }, []);

  const idle = onDark ? 'text-cream/35' : 'text-ink-soft';
  const hover = onDark ? 'group-hover:text-cream' : 'group-hover:text-ink';
  const rule = onDark ? 'bg-cream/30' : 'bg-ink/25';

  return (
    <nav
      aria-label="Page sections"
      className="fixed right-5 top-1/2 z-40 hidden -translate-y-1/2 lg:block xl:right-8"
    >
      <ul className="flex flex-col gap-0.5">
        {SECTIONS.map((s) => {
          const isActive = active === s.id;
          return (
            <li key={s.id}>
              <a
                href={`#${s.id}`}
                aria-current={isActive ? 'true' : undefined}
                className="group flex items-center justify-end gap-2.5 py-1.5"
              >
                <span
                  className={`font-mono text-[0.62rem] uppercase tracking-eyebrow transition-all duration-300 ease-calm ${
                    isActive
                      ? 'text-amber-deep opacity-100'
                      : `${idle} ${hover} opacity-0 group-hover:opacity-100`
                  }`}
                >
                  {s.label}
                </span>

                <span
                  className={`metric text-[0.62rem] transition-colors duration-300 ${
                    isActive ? 'text-amber-bright' : `${idle} ${hover}`
                  }`}
                >
                  {s.n}
                </span>

                <span
                  aria-hidden="true"
                  className={`h-px transition-all duration-300 ease-calm ${
                    isActive ? 'w-6 bg-amber-bright' : `w-2.5 ${rule} group-hover:w-4`
                  }`}
                />
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
};

export default SectionRail;
